import { useCallback, useEffect, useState } from "react";
import { productsApi } from "../../services/businessApi";
import { useToast } from "../../context/ToastContext";
import { useFeatureTheme } from "../../context/FeatureThemeContext";
import { Card } from "../../components/common/Card";
import { PageHeader } from "../../components/common/PageHeader";
import { DataTable } from "../../components/common/DataTable";
import { EmptyState } from "../../components/common/EmptyState";
import { LockedFeature } from "../../components/common/LockedFeature";
import { Button } from "../../components/common/Button";
import { BottleSizeManager } from "../../components/business/BottleSizeManager";
import { ProductPoolAdjuster } from "../../components/business/ProductPoolAdjuster";
import { ProductActivity } from "../../components/business/ProductActivity";
import { vocabularyFor, countOfUnits, weightLabel } from "../../services/productVocabulary";

/**
 * Wax, propolis and pollen (#430 slice 2).
 *
 * ── THE SECOND DOOR ──────────────────────────────────────────────────
 *
 * #430 decided two things, and this is the half that protects the
 * primary path. The data model generalises -- one chain under honey and
 * everything else -- but the NAVIGATION does not:
 *
 *   "Honey keeps its own front door. It is the main event for nearly
 *    every account, and the Honey tab was JUST simplified (#402, #410)
 *    precisely because it was doing too much. Piping wax and propolis
 *    through it would undo that within a release."
 *
 * So this is a separate destination, and honey never appears on it. A
 * keeper who sells only honey should never meet this screen.
 *
 * ── AND IT ONLY EXISTS ONCE IT IS EARNED ─────────────────────────────
 *
 * "Other products get their own place, visible only when the account
 * actually has some. An account that has never recorded a wax harvest
 * should not see a wax screen."
 *
 * The Sidebar hides the link until `otherProducts` is non-empty, which
 * the theme payload answers per account. This page still handles being
 * reached directly -- a bookmark, a link, an account switch -- because a
 * route that 404s on a URL somebody kept is its own small betrayal.
 *
 * The rule is wider than "has a balance", on purpose. A balance alone
 * would make the door VANISH the moment somebody sold their whole stock,
 * taking the history with it. See other_products_with_activity.
 *
 * ── THREE PRODUCTS, AND NOT A FOURTH ─────────────────────────────────
 *
 * Nucs, queens, colonies and pollination are #478 and must not appear
 * here until it is answered -- no tab, no greyed-out option, no "coming
 * soon". #430's own rule is that offering a thing that goes nowhere is
 * worse than not offering it, which is the entire reason three stranded
 * harvest types were a problem in the first place.
 *
 * Nothing on this page names them, and nothing needs to: the page
 * renders whatever the server lists, and V120's CHECK constraints make
 * those four values the only ones that can exist.
 */
export function OtherProductsPage() {
  const { notify } = useToast();
  const { hasFeature, canChangeLists } = useFeatureTheme();
  const enabled = hasFeature("HoneyChain");
  /* null is "not loaded yet", [] is "nothing to show". Different things,
     and conflating them is how a failed load renders as an empty state
     that says the data is gone (#197's lesson). */
  const [products, setProducts] = useState(null);
  const [failed, setFailed] = useState(false);
  /* Which product's sizes are being managed, and whether the add dialog
     is open on it. One at a time: the control belongs to a card, and two
     open managers would make "Add" ambiguous. */
  const [managing, setManaging] = useState(null);
  const [adding, setAdding] = useState(null);

  /* ONE LOADER, used by the first render and by every change below it.
     The first version of this had the effect and the reload duplicating
     the same promise chain with slightly different error handling, which
     is two code paths for one job and the kind of pair that drifts. */
  const load = useCallback(() => {
    return productsApi.list()
      .then((r) => { setProducts(r.products || []); setFailed(false); })
      .catch(() => {
        setProducts(null);
        setFailed(true);
        notify("Couldn't load your other products just now", "warning");
      });
    // notify is stable from the provider; listing it would rebuild this
    // on every toast and re-fetch with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Only ask for data the account can use. A locked page still renders (blurred
       preview), but its requests were refused by the server and logged as 403s in
       the console; `enabled` also re-runs this once the plan has loaded. */
  useEffect(() => { if (enabled) load(); }, [load, enabled]);

  return (
    <div>
      <PageHeader title="Wax, propolis & pollen" />

      {/* The preview names the BENEFIT rather than the feature, the rule
          the Expenditures lock already follows -- nobody upgrades to be
          given more forms. */}
      <LockedFeature
        enabled={enabled}
        featureLabel="Honey Chain"
        previewText="Size, pool and sell what comes off the hive besides honey — rendered wax, propolis tinctures, dried pollen."
      >
        {failed ? (
          <Card>
            <p style={{ margin: 0 }}>
              Couldn&apos;t load this just now. Nothing is lost — try again in a moment.
            </p>
          </Card>
        ) : products === null ? (
          <Card><p style={{ margin: 0 }}>Loading…</p></Card>
        ) : products.length === 0 ? (
          /* REACHED DIRECTLY WITH NOTHING TO SHOW. The Sidebar does not
             offer this link in that state, so arriving here means a
             bookmark or an account switch. Saying how a product gets
             here beats an empty table: the answer is "log a harvest",
             and that is not guessable from a blank screen. */
          <EmptyState
            title="Nothing but honey so far"
            description={
              "Wax, propolis and pollen appear here once you log a harvest of one, " +
              "or define a size for it. Honey stays on the Honey Pool page."
            }
          />
        ) : (
          products.map((p) => (
            <Card
              key={p.product_type}
              title={p.product_type}
              className="section-spacing"
              /* MANAGE, mirroring the Honey Pool's own control (#410) and
                 the varietal and equipment-type panels. Without it a
                 product has a balance and no way to define a container,
                 so nothing could be packaged or sold -- the gap that made
                 this page a window rather than a chain. */
              /* ONLY WHEN CLOSED. BottleSizeManager renders its own Done,
                 so a toggle here put two buttons called Done on one card
                 doing nearly the same thing -- the complaint #402 fixed on
                 the mobile Honey tab: "there are 3 buttons and 2 do the
                 exact same thing". One way in, one way out, and the way
                 out belongs to the panel that opened. */
              actions={managing === p.product_type || canChangeLists === false ? null : (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setManaging(p.product_type)}
                >
                  Manage sizes
                </Button>
              )}
            >
              {/* IN, CORRECTED, OUT (#430). Without these the balance was
                  read-only: the server has accepted a product_type on all
                  three pool writes since this slice's API half, and nothing
                  on either client called them, so wax could arrive only as
                  a side effect of logging a harvest and could never be
                  corrected at all.

                  HIDDEN WHILE SIZES ARE BEING MANAGED, so the card is
                  doing one job at a time -- the same reason the Manage
                  control itself steps aside for the panel's own Done. */}
              {managing === p.product_type ? null : (
<>
                  {/* The Honey Pool's pattern (#518): More menu, Add, and the
                      product's own verb, each opening a modal. */}
                  <ProductPoolAdjuster productType={p.product_type} sizes={p.sizes} onChanged={load} />
                </>
              )}

              <p style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
                {Number(p.pool_pounds || 0).toFixed(3)} lbs
                {/* The unit it is packaged in, beside pounds (#542). Pounds
                    stay the unit of record; propolis and pollen are sold
                    in ounces, so the ounces are shown too. */}
                {vocabularyFor(p.product_type).entryUnit !== "lb" && (
                  <span style={{ fontSize: "var(--font-size-md)", fontWeight: 400,
                                 color: "var(--color-text-muted)" }}>
                    {" "}({weightLabel(p.product_type, Number(p.pool_pounds || 0))})
                  </span>
                )}
              </p>
              {/* THE PRODUCT'S OWN WORD (#430). This said "unpackaged"
                  for everything; wax is unpoured, pollen unpacked. */}
              <p style={{ margin: "0 0 var(--space-3)", color: "var(--color-text-muted)" }}>
                {vocabularyFor(p.product_type).raw} and available
              </p>


              {/* THREE DECIMALS, not two. A 1 oz propolis tincture is
                  0.0625 lb, and V120 widened the column to hold that
                  exactly rather than rounding it to 0.063. Showing two
                  decimals here would throw away the precision the
                  migration was written to keep. */}
              <DataTable
                rows={p.sizes}
                loading={false}
                rowKey="bottle_size_id"
                emptyTitle={`No ${p.product_type.toLowerCase()} sizes yet`}
                emptyDescription={
                  `A size is how much one ${vocabularyFor(p.product_type).unit} holds. ` +
                  `Add one with Manage sizes before you ` +
                  `${vocabularyFor(p.product_type).verb.toLowerCase()} any.`
                }
                columns={[
                  { key: "label", header: "Size" },
                  /* IN THE PRODUCT'S OWN UNIT (4 Oct): pollen jars are
                     sold by the ounce, wax blocks by weight. Showing a
                     4 oz jar as "0.25 lb" hands back a different number
                     than the one that was typed in. weightLabel also
                     trims the conversion's trailing zeroes -- "4.0000 oz"
                     claims a precision nobody entered. */
                  { key: "pounds", header: "Each",
                    render: (r) => weightLabel(p.product_type, r.pounds) },
                  /* on_hand can go NEGATIVE, and it is shown rather than
                     clamped. The pool arithmetic already permits it --
                     bottling beyond recorded stock is a real thing a
                     keeper does, and V053 stores the shortfall rather
                     than refusing the entry. A negative here is a
                     COUNT THAT NEEDS CORRECTING, and hiding it behind a
                     zero is how it stays wrong. */
                  /* THE COUNT CARRIES THE NOUN (#430), so a row reads
                     "12 blocks" rather than a bare 12 under a header
                     borrowed from honey. countOfUnits gets a NEGATIVE
                     right too -- "-1 block", not "-1 blocks" -- which
                     matters on a number somebody is already puzzled by. */
                  { key: "on_hand", header: "On the shelf",
                    render: (r) => countOfUnits(p.product_type, r.on_hand) },
                ]}
              />

              {managing === p.product_type ? (
                <div style={{ marginTop: "var(--space-3)" }}>
                  <Button size="sm" onClick={() => setAdding(p.product_type)}>
                    {`Add a ${vocabularyFor(p.product_type).unit} size`}
                  </Button>
                  <BottleSizeManager
                    productType={p.product_type}
                    sizes={p.sizes}
                    adding={adding === p.product_type}
                    onAddingClose={() => setAdding(null)}
                    onChanged={load}
                    onClose={() => setManaging(null)}
                  />
                </div>
              ) : null}
            </Card>
          ))
        )}
        {/* ONE history for every product (#518), replacing the per-card
            panes (#495). */}
        {products && products.length > 0 ? (
          <ProductActivity
            productTypes={products.map((p) => p.product_type)}
            refreshKey={products.map((p) => p.pool_pounds).join(",")}
          />
        ) : null}
      </LockedFeature>
    </div>
  );
}
